/* ==========================================================================
   Greasy Miniatures — section behaviour
   Vanilla JS, no dependencies, no build step. Companion to
   greasy-theme.js, which owns the cart count and the header height.

   Everything here is an ENHANCEMENT. The header's dropdowns, the mobile
   drawer, the search popover and the hero slideshow are all native markup
   (<details> and a scroll-snap strip) that works with this file absent.
   What it adds is the manners a storefront is expected to have:

     - Escape closes what is open, and focus goes back to its trigger
     - opening one dropdown closes its siblings
     - a click outside an open dropdown dismisses it
     - the page behind the drawer does not scroll
     - the hero's dots become real controls, and autoplay exists at all
     - the goo rests while the tab is in the background
     - product rails can be dragged with a mouse

   If any of that throws, the navigation still navigates, and the goo keeps
   dripping.
   ========================================================================== */

(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ======================================================================
     HEADER — dropdowns, search, drawer
     ====================================================================== */

  var PANEL_SELECTOR = '[data-gm-mega], [data-gm-search], [data-gm-drawer]';

  function openPanels() {
    return Array.prototype.filter.call(
      document.querySelectorAll(PANEL_SELECTOR),
      function (details) {
        return details.open;
      }
    );
  }

  function close(details) {
    if (details && details.open) details.open = false;
  }

  /* Per-element wiring. Guarded, because the theme editor re-renders a
     section on every edit and this runs again each time — an unguarded
     binding would stack up a listener per edit. */
  function bindPanels(scope) {
    var panels = (scope || document).querySelectorAll(PANEL_SELECTOR);

    Array.prototype.forEach.call(panels, function (details) {
      if (details.dataset.gmBound === 'true') return;
      details.dataset.gmBound = 'true';

      var isDrawer = details.hasAttribute('data-gm-drawer');

      details.addEventListener('toggle', function () {
        if (!details.open) {
          if (isDrawer) lockScroll(false);
          return;
        }

        /* Only one thing open at a time: two open dropdowns overlap, and
           an open drawer under an open dropdown traps the pointer. */
        openPanels().forEach(function (other) {
          if (other !== details) close(other);
        });

        lockScroll(isDrawer);

        var input = details.querySelector('[data-gm-search-input]');
        if (input) {
          /* After the toggle paints, or Safari drops the focus call. */
          window.requestAnimationFrame(function () {
            input.focus();
          });
        }
      });

      if (isDrawer) {
        /* A tap on a link navigates, but on a same-page anchor nothing
           would appear to happen with the drawer still over the content. */
        details.addEventListener('click', function (event) {
          if (event.target.closest('a')) close(details);
        });
      }
    });
  }

  /* Document-level manners, bound exactly once. */
  function bindHeaderGlobals() {
    /* Escape closes the innermost open panel and returns focus to the
       summary that opened it — otherwise focus is left inside a panel that
       is no longer on screen. */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = openPanels();
      if (!open.length) return;

      var target = open[open.length - 1];
      var summary = target.querySelector('summary');
      close(target);
      if (summary) summary.focus();
    });

    /* Click away. Pointerdown rather than click, so it fires before a link
       inside the panel is followed. */
    document.addEventListener('pointerdown', function (event) {
      openPanels().forEach(function (details) {
        if (!details.contains(event.target)) close(details);
      });
    });

    /* A drawer left open while the viewport crosses into the desktop
       layout would be an orphaned fixed panel over the page. */
    window.addEventListener(
      'resize',
      function () {
        if (window.innerWidth < 1024) return;
        openPanels().forEach(function (details) {
          if (details.hasAttribute('data-gm-drawer')) close(details);
        });
      },
      { passive: true }
    );
  }

  var scrollLocked = false;

  function lockScroll(shouldLock) {
    if (shouldLock === scrollLocked) return;
    scrollLocked = shouldLock;
    document.documentElement.style.overflow = shouldLock ? 'hidden' : '';
  }

  /* ======================================================================
     HERO SLIDESHOW
     The track is already a working scroll-snap strip. This wires the dots
     to it, keeps them in sync with whatever the user has scrolled to, and
     runs autoplay when the section asks for it.
     ====================================================================== */

  function initSlideshow(root) {
    if (root.dataset.gmBound === 'true') return;
    root.dataset.gmBound = 'true';

    var track = root.querySelector('[data-gm-slideshow-track]');
    var dotsWrap = root.querySelector('[data-gm-slideshow-dots]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) return;

    var dots = dotsWrap
      ? Array.prototype.slice.call(dotsWrap.querySelectorAll('[data-gm-slide-to]'))
      : [];
    var current = 0;
    var timer = null;
    var stopped = false;

    function goTo(index, smooth) {
      var target = Math.max(0, Math.min(index, slides.length - 1));
      track.scrollTo({
        left: track.clientWidth * target,
        behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto'
      });
    }

    function markCurrent(index) {
      current = index;
      dots.forEach(function (dot, i) {
        if (i === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        stop();
        goTo(parseInt(dot.dataset.gmSlideTo, 10), true);
      });
    });

    /* Left/right on the focused track, which is what a keyboard user will
       reach first — the dots are after it in the tab order. */
    track.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      stop();
      goTo(current + (event.key === 'ArrowRight' ? 1 : -1), true);
    });

    /* Which slide is showing is whatever the scroll position says, however
       it got there — a swipe, a dot, a keypress or autoplay. One source of
       truth, so the dots can never disagree with the view. */
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              markCurrent(slides.indexOf(entry.target));
            }
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach(function (slide) {
        observer.observe(slide);
      });
    }

    /* ---- Autoplay ---- */

    function advance() {
      var next = current + 1;
      goTo(next >= slides.length ? 0 : next, true);
    }

    function start() {
      if (stopped || timer || reducedMotion.matches) return;
      if (root.dataset.autoplay !== 'true') return;
      var interval = parseInt(root.dataset.interval, 10) || 7000;
      timer = window.setInterval(advance, interval);
    }

    function pause() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    /* Any deliberate interaction ends autoplay for good. Resuming it after
       someone has chosen a slide takes the page away from them. */
    function stop() {
      stopped = true;
      pause();
    }

    ['pointerdown', 'keydown', 'focusin'].forEach(function (type) {
      root.addEventListener(type, stop, { once: true });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        pause();
      } else {
        start();
      }
    });

    /* No point animating a hero nobody can see. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              start();
            } else {
              pause();
            }
          });
        },
        { threshold: 0.25 }
      ).observe(root);
    } else {
      start();
    }

    reducedMotion.addEventListener('change', function () {
      if (reducedMotion.matches) pause();
    });
  }

  /* ======================================================================
     GOO — stop paying for frames nobody can see

     Every piece of goo is an SVG group under a filter, and one moving drip
     re-runs that whole filter for its group, so goo is the most expensive
     thing on the page by a wide margin.

     The obvious saving would be to freeze goo that is scrolled out of view.
     That is DELIBERATELY not done: the drips are the point of the design,
     and gating them means a piece can be caught starting up, or sitting
     still because an observer has not fired yet. Visible goo always moves.

     What is left is the one case where nothing is lost, because there is
     nothing to see: a backgrounded tab. It renders no frames but the
     animations keep the filters re-running, so everything rests until the
     tab comes back.

     gm-goo-art--still is the goo system's own per-instance off switch — the
     class greasy-goo-divider takes as `still: true`, and the one the mobile
     and reduced-motion rules use. Instances authored still are left out of
     this entirely, so they can never be woken by it.
     ====================================================================== */

  var GOO_STILL = 'gm-goo-art--still';
  var gooManaged = [];

  function initGoo(scope) {
    var found = scope.querySelectorAll('.gm-goo-art');
    if (!found.length) return;

    /* The theme editor replaces a section's nodes wholesale. */
    gooManaged = gooManaged.filter(function (art) {
      return art.isConnected !== false && document.contains(art);
    });

    Array.prototype.forEach.call(found, function (art) {
      if (gooManaged.indexOf(art) !== -1) return;
      if (art.classList.contains(GOO_STILL)) return;
      gooManaged.push(art);
    });
  }

  document.addEventListener('visibilitychange', function () {
    var hidden = document.hidden;
    gooManaged.forEach(function (art) {
      art.classList.toggle(GOO_STILL, hidden);
    });
  });

  /* ======================================================================
     RAILS — drag to scroll

     Product rows are one line at every width, so on a wide screen the row
     runs past the edge and has to be scrollable. Trackpads and touch already
     do that; a mouse does not, and the scrollbar is hidden, so without this
     a desktop visitor with a mouse can see there is more and has no way to
     reach it.

     Pointer Events, not mousedown: one code path covers mouse, pen and touch,
     and setPointerCapture keeps the drag alive when the cursor leaves the
     rail. Touch is excluded (pointerType 'touch') because the browser's own
     inertial scrolling is better than anything reimplemented here — this only
     takes over where native dragging does not exist.

     A drag must not fire the link under the cursor. The threshold below is
     what separates a click on a card from a drag of the rail: past it, the
     pointer is captured, scroll-snap is suspended so the rail tracks the
     hand 1:1, and the click that follows is swallowed once.
     ====================================================================== */

  var DRAG_THRESHOLD = 6;

  function initRail(rail) {
    if (rail.dataset.gmRail === 'true') return;
    rail.dataset.gmRail = 'true';

    var startX = 0;
    var startScroll = 0;
    var pointerId = null;
    var dragging = false;

    function overflows() {
      return rail.scrollWidth - rail.clientWidth > 1;
    }

    rail.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      if (!overflows()) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      dragging = false;
    });

    rail.addEventListener('pointermove', function (event) {
      if (pointerId === null || event.pointerId !== pointerId) return;

      var dx = event.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        rail.classList.add('gm-scroller--dragging');
        /* Snap points fight a drag: the rail would jump to the nearest card
           mid-gesture instead of following the hand. */
        rail.style.scrollSnapType = 'none';
        try {
          rail.setPointerCapture(pointerId);
        } catch (e) {
          /* Capture is a nicety; the drag still works without it. */
        }
      }

      rail.scrollLeft = startScroll - dx;
      event.preventDefault();
    });

    function endDrag(event) {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;

      if (dragging) {
        /* Swallow the click this gesture is about to produce, so letting go
           over a product card does not open it. */
        rail.addEventListener('click', swallow, true);
        rail.classList.remove('gm-scroller--dragging');
        /* Restoring snap re-settles the rail on the nearest card. */
        rail.style.scrollSnapType = '';
      }

      pointerId = null;
      dragging = false;
    }

    function swallow(event) {
      event.stopPropagation();
      event.preventDefault();
      rail.removeEventListener('click', swallow, true);
    }

    rail.addEventListener('pointerup', endDrag);
    rail.addEventListener('pointercancel', endDrag);

    /* Native drag-and-drop of the card images would hijack the gesture. */
    rail.addEventListener('dragstart', function (event) {
      if (dragging) event.preventDefault();
    });
  }

  /* ======================================================================
     RAIL HANDLE - the drag affordance, made visible

     The drag above has existed since the row became one line, and nothing
     on the page announced it. A hidden scrollbar plus a grab cursor that
     only shows up once the pointer is already over a card is not an
     affordance: the cut-off card at the right edge says there is more
     without saying how to get to it.

     This wires the handle under each row:

       - the thumb's WIDTH is the fraction of the row that fits on screen
         and its POSITION is where in the row you are, so the control also
         answers "how much more is there";
       - dragging the thumb maps thumb pixels to scroll pixels through the
         ratio between the track's spare travel and the row's, so the row
         keeps up with the hand exactly;
       - the two step buttons move by just under a screenful and disable
         themselves at each end.

     Geometry goes out as two custom properties rather than as pixel values,
     so the thumb is laid out by CSS percentages and nothing here has to be
     recomputed when the container resizes - only re-read.

     Snap is suspended for the duration of a thumb drag for the same reason
     it is for a card drag: mandatory snap points would yank the row to the
     nearest card mid-gesture. Restoring it re-settles on a card.
     ====================================================================== */

  /* Below this the thumb would be too small to grab. A sixteen-card row on a
     390px phone shows about 9% of itself, and 9% of that track is a 27px
     target; the floor puts it back to roughly 48px. It costs the thumb its
     honesty about how long the row is, which is worth less than being able
     to get hold of it. Desktop rarely reaches the floor - the same row at
     1440 genuinely shows 32%. */
  var MIN_THUMB = 0.16;

  /* Just under a screenful: enough that a click feels like progress, short
     enough to leave the card you were looking at in view for context. */
  var STEP_FRACTION = 0.8;

  function initRailControls(rail) {
    var wrap = rail.closest('[data-gm-rail-wrap]');
    if (!wrap) return;

    var controls = wrap.querySelector('[data-gm-rail-controls]');
    /* Guarded like every other binding here: the theme editor re-renders a
       section's markup in place and init runs again over it. */
    if (!controls || controls.dataset.gmBound === 'true') return;
    controls.dataset.gmBound = 'true';

    var track = controls.querySelector('[data-gm-rail-track]');
    var thumb = controls.querySelector('[data-gm-rail-thumb]');
    var steps = controls.querySelectorAll('[data-gm-rail-step]');
    if (!track || !thumb) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function behaviour() {
      return reduceMotion.matches ? 'auto' : 'smooth';
    }

    function maxScroll() {
      return rail.scrollWidth - rail.clientWidth;
    }

    var frame = null;

    function sync() {
      frame = null;

      var max = maxScroll();
      /* A row that fits has nothing to scroll, so the control would be a
         handle attached to nothing. */
      if (max <= 1) {
        controls.hidden = true;
        return;
      }
      controls.hidden = false;

      var visible = rail.clientWidth / rail.scrollWidth;
      if (visible < MIN_THUMB) visible = MIN_THUMB;
      if (visible > 1) visible = 1;

      var progress = rail.scrollLeft / max;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      thumb.style.setProperty('--gm-thumb-w', visible * 100 + '%');
      thumb.style.setProperty('--gm-thumb-p', progress);

      Array.prototype.forEach.call(steps, function (button) {
        var back = Number(button.dataset.gmRailStep) < 0;
        /* 1px of slack: scrollLeft is fractional on a zoomed or scaled
           viewport and never lands exactly on 0 or on max. */
        button.disabled = back
          ? rail.scrollLeft <= 1
          : rail.scrollLeft >= max - 1;
      });
    }

    /* Scroll fires far more often than the screen refreshes. */
    function schedule() {
      if (frame === null) frame = requestAnimationFrame(sync);
    }

    rail.addEventListener('scroll', schedule, { passive: true });

    /* ---- Step buttons -------------------------------------------------- */
    Array.prototype.forEach.call(steps, function (button) {
      button.addEventListener('click', function () {
        var step = Math.max(
          rail.clientWidth * STEP_FRACTION,
          /* Never less than one card, whatever the row is set to. */
          rail.firstElementChild ? rail.firstElementChild.offsetWidth : 0
        );
        rail.scrollBy({
          left: Number(button.dataset.gmRailStep) * step,
          behavior: behaviour()
        });
      });
    });

    /* ---- Dragging the thumb -------------------------------------------- */
    var dragId = null;
    var dragStartX = 0;
    var dragStartScroll = 0;
    var dragRatio = 0;

    thumb.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      var max = maxScroll();
      if (max <= 1) return;

      /* Scroll pixels per pixel of thumb travel. The thumb has less room to
         move than the row does, and this is the whole of the difference. */
      var travel = track.clientWidth - thumb.offsetWidth;
      if (travel <= 0) return;

      dragId = event.pointerId;
      dragStartX = event.clientX;
      dragStartScroll = rail.scrollLeft;
      dragRatio = max / travel;

      controls.classList.add('gm-rail__controls--dragging');
      rail.style.scrollSnapType = 'none';
      try {
        thumb.setPointerCapture(dragId);
      } catch (e) {
        /* Capture is a nicety; the drag still tracks without it. */
      }
      event.preventDefault();
    });

    thumb.addEventListener('pointermove', function (event) {
      if (dragId === null || event.pointerId !== dragId) return;
      rail.scrollLeft =
        dragStartScroll + (event.clientX - dragStartX) * dragRatio;
      event.preventDefault();
    });

    function endThumbDrag(event) {
      if (dragId === null || (event && event.pointerId !== dragId)) return;
      dragId = null;
      controls.classList.remove('gm-rail__controls--dragging');
      /* Restoring snap re-settles the row on the nearest card. */
      rail.style.scrollSnapType = '';
    }

    thumb.addEventListener('pointerup', endThumbDrag);
    thumb.addEventListener('pointercancel', endThumbDrag);

    /* ---- Clicking the bare track --------------------------------------- */
    track.addEventListener('pointerdown', function (event) {
      if (event.target.closest('[data-gm-rail-thumb]')) return;

      var max = maxScroll();
      if (max <= 1) return;

      var box = track.getBoundingClientRect();
      var travel = box.width - thumb.offsetWidth;
      if (travel <= 0) return;

      /* Centre the thumb on the click rather than putting its left edge
         there, which is what makes a click near the end reach the end. */
      var target = event.clientX - box.left - thumb.offsetWidth / 2;
      var progress = Math.min(1, Math.max(0, target / travel));
      rail.scrollTo({ left: progress * max, behavior: behaviour() });
    });

    /* ---- Staying correct ------------------------------------------------
       The thumb's width is a ratio of two measurements, so it is wrong the
       moment either changes: a window resize, a phone turning, or the theme
       editor changing the card width setting. */
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(schedule).observe(rail);
    } else {
      window.addEventListener('resize', schedule);
    }

    sync();
  }

  /* ======================================================================
     BOOT
     Re-runs on section load so the theme editor gets working behaviour
     after an owner edits a section, instead of a dead hero until reload.
     ====================================================================== */

  function init(scope) {
    var root = scope || document;
    bindPanels(root);
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-gm-slideshow]'),
      initSlideshow
    );
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-gm-rail]'),
      function (rail) {
        initRail(rail);
        initRailControls(rail);
      }
    );
    initGoo(root);
  }

  function boot() {
    bindHeaderGlobals();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor swaps a section's markup in place. The new nodes carry
     no bindings, and the globals above are already attached to document. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
